/**
 * Pixel-ingest hooks — every query goes through the typed `apiFetch`,
 * filtered server-side by `org_uuid` (the API injects it from the auth
 * context, never from caller params).
 *
 * Patterns follow plans/1.0.0/pixel-ingest/08-data-loading.md:
 *
 *   - Org-scoped query keys via `pixelKeys.*` (prevents cross-org
 *     cache leaks on org switch).
 *   - `placeholderData: keepPreviousData` on every hook with a
 *     user-controllable filter (range / dimension / form factor)
 *     so the card doesn't blank during filter swaps.
 *   - `useSitePixelLive` polls every 5s — interval returns `false`
 *     while the tab is in the background to avoid burning quota on
 *     hidden tabs.
 *   - `useSitePixelBundle` is the cold-load fast path — wraps the
 *     bundle endpoint. The site-home Analytics view is the only
 *     caller; per-card components consume slices via props.
 */
import {
  type UseMutationResult,
  type UseQueryResult,
  keepPreviousData,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useActiveOrg } from './active-org';
import { pixelKeys } from './query-keys';

// ─── Response shapes (mirror packages/tinybird/src/pixel.ts) ──────────────

export interface PixelKpis {
  visitors: number;
  sessions: number;
  pageviews: number;
  pages_per_session: number | null;
  bounce_rate: number | null;
  avg_session_duration_ms: number | null;
}

export interface PixelTrendRow {
  day: string;
  form_factor: string;
  visitors: number;
  sessions: number;
  pageviews: number;
}

/** Per-day series of the six KPI tile values — used to draw real
 *  sparkbars under each tile instead of the hand-tuned watermark. */
export interface PixelKpisTrendRow {
  day: string;
  visitors: number;
  sessions: number;
  pageviews: number;
  pages_per_session: number | null;
  bounce_rate: number | null;
  avg_session_duration_ms: number | null;
}

/** Per-country CWV p75 + sample count for the Performance overview's
 *  Country Ranking card. */
export interface PixelPerfByCountryRow {
  country_code: string;
  lcp_p75: number | null;
  lcp_sample_count: number;
  cls_p75: number | null;
  cls_sample_count: number;
  inp_p75: number | null;
  inp_sample_count: number;
  ttfb_p75: number | null;
  ttfb_sample_count: number;
  fcp_p75: number | null;
  fcp_sample_count: number;
}

/** Per-page CWV p75 + sample count (+ a pageviews popularity count) for
 *  the Performance overview's Page ranking + Page popularity cards. */
export interface PixelPerfByPageRow {
  page_path: string;
  pageviews: number;
  lcp_p75: number | null;
  lcp_sample_count: number;
  cls_p75: number | null;
  cls_sample_count: number;
  inp_p75: number | null;
  inp_sample_count: number;
  ttfb_p75: number | null;
  ttfb_sample_count: number;
  fcp_p75: number | null;
  fcp_sample_count: number;
}

export interface PixelPerfRow {
  form_factor: string;
  lcp_p75: number | null;
  lcp_good_density: number | null;
  lcp_sample_count: number;
  cls_p75: number | null;
  cls_good_density: number | null;
  cls_sample_count: number;
  inp_p75: number | null;
  inp_good_density: number | null;
  inp_sample_count: number;
  ttfb_p75: number | null;
  ttfb_good_density: number | null;
  ttfb_sample_count: number;
  fcp_p75: number | null;
  fcp_good_density: number | null;
  fcp_sample_count: number;
  loaf_p75: number | null;
}

export interface PixelPerfTrendRow extends PixelPerfRow {
  day: string;
}

/** One traffic profile — a visitor cohort rather than a page. Same
 *  vitals shape as a perf row, plus the reach numbers the surface
 *  ranks by. `affected_sessions` is sessions x the share of Core Web
 *  Vital samples not rated good, so it's an estimate of how many
 *  visits hit a failing vital, not an exact count. */
/** One of the worst sessions on a day for a single metric — a row
 *  behind a bad point on the trend chart. `started_at` doubles as the
 *  `around` hint the session view needs to bound its scan. */
export interface PixelWorstSessionRow {
  day: string;
  session_uuid: string;
  started_at: string;
  page_path: string;
  form_factor: string;
  country_code: string;
  network_type: string;
  value: number;
}

export interface PixelProfileRow extends PixelPerfRow {
  network_type: string;
  network_save_data: number;
  sessions: number;
  samples: number;
  poor_share: number | null;
  affected_sessions: number;
}

export interface PixelBreakdownRow {
  bucket: string;
  pageviews: number;
  visitors: number;
  sessions: number;
  lcp_p75: number | null;
  lcp_good_density: number | null;
}

export interface PixelTopPagesRow {
  url: string;
  pageviews: number;
  entries: number;
  exits: number;
  visitors: number;
  sessions: number;
  avg_engagement_ms: number | null;
}

/** Traffic KPIs for one page_path — top-pages row without the url. */
export interface PixelPageTrafficRow {
  pageviews: number;
  entries: number;
  exits: number;
  visitors: number;
  sessions: number;
  avg_engagement_ms: number | null;
}

/** Per-day traffic series for one page_path (no form-factor split). */
export interface PixelPageTrafficTrendRow {
  day: string;
  pageviews: number;
  entries: number;
  exits: number;
  visitors: number;
  sessions: number;
}

/** One session that viewed a given page_path. Timestamps are ClickHouse
 *  DateTime strings ("YYYY-MM-DD HH:MM:SS"); `started_at` doubles as the
 *  `around` hint for the session-events query. The vitals are what this
 *  visitor experienced on the target page (null = no web-vitals beacon). */
export interface PixelPageSessionRow {
  session_uuid: string;
  /** Durable user id — only set when the site calls cwv.identify(). */
  profile_uuid: string;
  /** Always resolves, but rotates at UTC midnight. */
  device_uuid: string;
  started_at: string;
  ended_at: string;
  entry_path: string;
  exit_path: string;
  pageviews: number;
  engagement_ms: number;
  country_code: string;
  form_factor: string;
  browser_family: string;
  channel: string;
  referrer_host: string;
  lcp: number | null;
  cls: number | null;
  inp: number | null;
}

/** One raw event in a session's ordered timeline. The vitals columns
 *  are set on web-vitals beacons (event_kind 'visibility', one metric
 *  per row) and null on everything else. The nav_* timings and
 *  bfcache_hit only land on hard pageviews; network_* rides every
 *  beacon in the session. */
export interface PixelSessionEventRow {
  timestamp: string;
  event_kind: string;
  event_name: string;
  event_props: string;
  page_path: string;
  engagement_ms: number;
  scroll_depth_pct: number;
  outgoing_host: string;
  country_code: string;
  region_code: string;
  form_factor: string;
  browser_family: string;
  os_family: string;
  channel: string;
  referrer_host: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  is_session_start: number;
  is_session_end: number;
  pageview_count_in_session: number;
  lcp: number | null;
  cls: number | null;
  inp: number | null;
  active_visible_ms: number;
  network_type: string;
  network_save_data: number;
  lcp_rating: string;
  cls_rating: string;
  inp_rating: string;
  ttfb: number | null;
  ttfb_rating: string;
  fcp: number | null;
  fcp_rating: string;
  longest_loaf_ms: number | null;
  loaf_script_host: string;
  nav_dns_ms: number | null;
  nav_tcp_ms: number | null;
  nav_request_ms: number | null;
  nav_response_ms: number | null;
  nav_dom_interactive_ms: number | null;
  nav_dom_complete_ms: number | null;
  nav_redirect_count: number;
  nav_type: string;
  bfcache_hit: number;
}

export interface PixelTopEventsRow {
  event_kind: string;
  event_name: string;
  event_count: number;
  visitors: number;
  sessions: number;
}

export interface PixelLiveMinute {
  /** UTC minute, ISO 8601. */
  minute: string;
  pageviews: number;
  visitors: number;
}

export interface PixelLiveListRow {
  /** Page path, referrer host / UTM source ('' = direct), or country code. */
  label: string;
  visitors: number;
}

export interface PixelLiveVital {
  p75: number | null;
  samples: number;
}

export interface PixelLive {
  active_visitors: number;
  active_sessions: number;
  beacons_5m: number;
  // Detail sections are optional — the API serves counts-only until
  // the pixel_live_detail pipe is deployed, and the page renders the
  // empty shell for whatever is absent.
  minutes?: PixelLiveMinute[];
  top_pages?: PixelLiveListRow[];
  sources?: PixelLiveListRow[];
  countries?: PixelLiveListRow[];
  vitals?: { lcp: PixelLiveVital; inp: PixelLiveVital; cls: PixelLiveVital };
}

export interface PixelAttributionRow {
  target_kind: string;
  target_value: string;
  occurrence_count: number;
  p75_value: number | null;
}

export interface PixelHealthToday {
  day_utc: string;
  sessions_today: number;
  visitors_today: number;
  beacons_today: number;
  last_beacon_at: string;
}

export interface PixelBundle {
  kpis?: PixelKpis | null;
  kpis_trend?: PixelKpisTrendRow[] | null;
  trend?: PixelTrendRow[] | null;
  perf?: PixelPerfRow[] | null;
  channels?: PixelBreakdownRow[] | null;
  geo?: PixelBreakdownRow[] | null;
  devices?: PixelBreakdownRow[] | null;
  top_pages?: PixelTopPagesRow[] | null;
  top_events?: PixelTopEventsRow[] | null;
  live?: PixelLive | null;
  health?: PixelHealthToday | null;
}

// ─── Query-string helpers ─────────────────────────────────────────────────

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) sp.set(k, v.join(','));
    } else {
      sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

interface CommonOpts {
  days?: number;
  formFactors?: string[];
}

/** Geo / network filter options. Sites'-perf + sites'-perf-trend pipes
 *  flip onto the raw-events filtered pipe when either array is non-
 *  empty — see hasGeoOrNetworkFilter in packages/tinybird/src/pixel.ts.
 *  Threaded into the query key so React Query treats filter swaps as
 *  distinct cache entries. */
/**
 * Visitor-dimension filters on the site perf read. Any of them routes the
 * API onto the raw-events pipe; all absent keeps the MV fast path.
 * Mirrors `PerfQuery` in apps/api/src/routes/pixel.ts — a filter added
 * here without its counterpart there is silently dropped, which would
 * report a wider audience than asked for.
 */
interface PerfFilterOpts extends CommonOpts {
  countryCodes?: string[];
  networkTypes?: string[];
  saveData?: number;
  regionCodes?: string[];
  browserFamilies?: string[];
  osFamilies?: string[];
  channels?: string[];
  referrerHosts?: string[];
  utmSources?: string[];
  utmMediums?: string[];
  utmCampaigns?: string[];
  pagePaths?: string[];
}

// ─── Site-scoped hooks ────────────────────────────────────────────────────

export function useSitePixelKpis(
  siteUuid: string | null,
  opts: CommonOpts = {},
): UseQueryResult<PixelKpis | null> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.kpis(orgId, siteUuid ?? '', opts),
    enabled: !!siteUuid,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelKpis | null>(
        `/v1/sites/${siteUuid}/pixel/kpis${qs({ days: opts.days, form_factors: opts.formFactors })}`,
      ),
  });
}

/** Per-day series for the six site KPI tiles. Drives the real
 *  sparkbars on the Traffic surface. */
export function useSitePixelKpisTrend(
  siteUuid: string | null,
  opts: CommonOpts & { enabled?: boolean; initialData?: PixelKpisTrendRow[] } = {},
): UseQueryResult<PixelKpisTrendRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, initialData, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.kpisTrend(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    ...(initialData ? { initialData } : {}),
    queryFn: () =>
      apiFetch<PixelKpisTrendRow[]>(
        `/v1/sites/${siteUuid}/pixel/kpis-trend${qs({
          days: keyOpts.days,
          form_factors: keyOpts.formFactors,
        })}`,
      ),
  });
}

export function useSitePixelTrend(
  siteUuid: string | null,
  opts: CommonOpts & { enabled?: boolean; initialData?: PixelTrendRow[] } = {},
): UseQueryResult<PixelTrendRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, initialData, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.trend(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    ...(initialData ? { initialData } : {}),
    queryFn: () =>
      apiFetch<PixelTrendRow[]>(
        `/v1/sites/${siteUuid}/pixel/trend${qs({ days: keyOpts.days, form_factors: keyOpts.formFactors })}`,
      ),
  });
}

export function useSitePixelPerf(
  siteUuid: string | null,
  opts: PerfFilterOpts & { enabled?: boolean } = {},
): UseQueryResult<PixelPerfRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.perf(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPerfRow[]>(
        `/v1/sites/${siteUuid}/pixel/perf${qs({
          days: keyOpts.days,
          form_factors: keyOpts.formFactors,
          country_codes: keyOpts.countryCodes,
          network_types: keyOpts.networkTypes,
          save_data: keyOpts.saveData,
          region_codes: keyOpts.regionCodes,
          browser_families: keyOpts.browserFamilies,
          os_families: keyOpts.osFamilies,
          channels: keyOpts.channels,
          referrer_hosts: keyOpts.referrerHosts,
          utm_sources: keyOpts.utmSources,
          utm_mediums: keyOpts.utmMediums,
          utm_campaigns: keyOpts.utmCampaigns,
          page_paths: keyOpts.pagePaths,
        })}`,
      ),
  });
}

/** Per-country CWV p75 ranking for the Performance overview's
 *  Country Ranking card. Honours the same form-factor + network
 *  filters as the headline perf query so the ranking reflects the
 *  active audience. */
export function useSitePixelPerfByCountry(
  siteUuid: string | null,
  opts: PerfFilterOpts & { limit?: number; enabled?: boolean } = {},
): UseQueryResult<PixelPerfByCountryRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.perfByCountry(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPerfByCountryRow[]>(
        `/v1/sites/${siteUuid}/pixel/perf-by-country${qs({
          days: keyOpts.days,
          form_factors: keyOpts.formFactors,
          network_types: keyOpts.networkTypes,
          limit: keyOpts.limit,
        })}`,
      ),
  });
}

/** Per-page CWV p75 ranking for the Performance overview's Page ranking
 *  + Page popularity cards. Honours the same form-factor + network
 *  filters as the country ranking so both reflect the active audience. */
export function useSitePixelPerfByPage(
  siteUuid: string | null,
  opts: PerfFilterOpts & { limit?: number; enabled?: boolean } = {},
): UseQueryResult<PixelPerfByPageRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.perfByPage(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPerfByPageRow[]>(
        `/v1/sites/${siteUuid}/pixel/perf-by-page${qs({
          days: keyOpts.days,
          form_factors: keyOpts.formFactors,
          network_types: keyOpts.networkTypes,
          limit: keyOpts.limit,
        })}`,
      ),
  });
}

/**
 * Traffic profiles — real-user CWV grouped by visitor cohort (device x
 * connection class x data saver), worst first. Unlike the perf hooks,
 * `countryCodes` does not fall back to a raw-events scan here: country
 * is in the profile MV's sorting key, so narrowing to a market stays
 * on the fast path.
 */
export function useSitePixelProfiles(
  siteUuid: string | null,
  opts: PerfFilterOpts & { minSessions?: number; limit?: number; enabled?: boolean } = {},
): UseQueryResult<PixelProfileRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.profiles(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelProfileRow[]>(
        `/v1/sites/${siteUuid}/pixel/profiles${qs({
          days: keyOpts.days,
          form_factors: keyOpts.formFactors,
          country_codes: keyOpts.countryCodes,
          min_sessions: keyOpts.minSessions,
          limit: keyOpts.limit,
        })}`,
      ),
  });
}

/**
 * The worst sessions per day for one metric — what the trend chart's
 * day picker offers when the reader clicks a bad point. Off by default
 * (`enabled`) so it only costs a raw-events scan on the surface that
 * actually renders the picker.
 */
export function useSiteWorstSessions(
  siteUuid: string | null,
  opts: {
    metric: string;
    days?: number;
    formFactors?: string[];
    perDay?: number;
    enabled?: boolean;
  },
): UseQueryResult<PixelWorstSessionRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.worstSessions(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelWorstSessionRow[]>(
        `/v1/sites/${siteUuid}/pixel/worst-sessions${qs({
          metric: keyOpts.metric,
          days: keyOpts.days,
          form_factors: keyOpts.formFactors,
          per_day: keyOpts.perDay,
        })}`,
      ),
  });
}

/**
 * Daily CWV p75 series for a site — one row per (day, form_factor).
 * Site-scoped sibling of {@link usePagePixelPerfTrend}; drives the
 * Performance overview's per-tile bar histogram + the metric-selector
 * trend chart.
 */
export function useSitePixelPerfTrend(
  siteUuid: string | null,
  opts: PerfFilterOpts & { enabled?: boolean } = {},
): UseQueryResult<PixelPerfTrendRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.perfTrend(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPerfTrendRow[]>(
        `/v1/sites/${siteUuid}/pixel/perf-trend${qs({
          days: keyOpts.days,
          form_factors: keyOpts.formFactors,
          country_codes: keyOpts.countryCodes,
          network_types: keyOpts.networkTypes,
          save_data: keyOpts.saveData,
          region_codes: keyOpts.regionCodes,
          browser_families: keyOpts.browserFamilies,
          os_families: keyOpts.osFamilies,
          channels: keyOpts.channels,
          referrer_hosts: keyOpts.referrerHosts,
          utm_sources: keyOpts.utmSources,
          utm_mediums: keyOpts.utmMediums,
          utm_campaigns: keyOpts.utmCampaigns,
          page_paths: keyOpts.pagePaths,
        })}`,
      ),
  });
}

export function useSitePixelBreakdown(
  siteUuid: string | null,
  dimension: string,
  opts: CommonOpts & {
    limit?: number;
    sort?: 'pageviews' | 'visitors' | 'sessions';
    /** Gate the fetch (e.g. an inactive tab or a 1.1 stub). Combined
     * with the `!!siteUuid` guard. Defaults to true. */
    enabled?: boolean;
    /** Seed from a bundle fetch so the default tab paints instantly
     * on cold load, then revalidates. Excluded from the query key. */
    initialData?: PixelBreakdownRow[];
  } = {},
): UseQueryResult<PixelBreakdownRow[]> {
  const orgId = useActiveOrg().orgId;
  // `enabled`/`initialData` are config, not query inputs — keep them
  // out of the key or every tab would cache under a distinct entry.
  const { enabled, initialData, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.breakdown(orgId, siteUuid ?? '', dimension, keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    ...(initialData ? { initialData } : {}),
    queryFn: () =>
      apiFetch<PixelBreakdownRow[]>(
        `/v1/sites/${siteUuid}/pixel/breakdown${qs({
          dimension,
          days: keyOpts.days,
          limit: keyOpts.limit,
          sort: keyOpts.sort,
        })}`,
      ),
  });
}

export function useSiteTopPages(
  siteUuid: string | null,
  opts: {
    days?: number;
    limit?: number;
    sort?: 'pageviews' | 'entries' | 'exits';
    enabled?: boolean;
    initialData?: PixelTopPagesRow[];
  } = {},
): UseQueryResult<PixelTopPagesRow[]> {
  const orgId = useActiveOrg().orgId;
  const { enabled, initialData, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.topPages(orgId, siteUuid ?? '', keyOpts),
    enabled: !!siteUuid && (enabled ?? true),
    placeholderData: keepPreviousData,
    ...(initialData ? { initialData } : {}),
    queryFn: () =>
      apiFetch<PixelTopPagesRow[]>(`/v1/sites/${siteUuid}/pixel/top-pages${qs(keyOpts)}`),
  });
}

// ─── Page-focused traffic hooks (site + raw page_path scoped) ─────────────
//
// These power `/traffic/page` + `/traffic/session/*`. They are keyed by
// the raw page_path string (not a pg-* entity uuid) because RUM paths
// aren't guaranteed to have a registered page entity.

export function usePagePixelTraffic(
  siteUuid: string | null,
  opts: { path: string; days?: number },
): UseQueryResult<PixelPageTrafficRow | null> {
  const orgId = useActiveOrg().orgId;
  const { path, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.pageTraffic(orgId, siteUuid ?? '', path, keyOpts),
    enabled: !!siteUuid && !!path,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPageTrafficRow | null>(
        `/v1/sites/${siteUuid}/pixel/page-traffic${qs({ path, days: keyOpts.days })}`,
      ),
  });
}

export function usePagePixelTrafficTrend(
  siteUuid: string | null,
  opts: { path: string; days?: number; enabled?: boolean },
): UseQueryResult<PixelPageTrafficTrendRow[]> {
  const orgId = useActiveOrg().orgId;
  const { path, enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.pageTrafficTrend(orgId, siteUuid ?? '', path, keyOpts),
    enabled: !!siteUuid && !!path && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPageTrafficTrendRow[]>(
        `/v1/sites/${siteUuid}/pixel/page-trend${qs({ path, days: keyOpts.days })}`,
      ),
  });
}

/** Page-scoped sibling of {@link useSitePixelBreakdown} — same row shape
 *  (lcp columns are always null on this raw-backed surface), so
 *  BreakdownCard renders it unchanged. Window is limited by plan
 *  retention (30 days / 12 months), unlike the MV-backed site version. */
export function usePagePixelTrafficBreakdown(
  siteUuid: string | null,
  dimension: string,
  opts: {
    path: string;
    days?: number;
    limit?: number;
    sort?: 'pageviews' | 'visitors' | 'sessions';
    enabled?: boolean;
  },
): UseQueryResult<PixelBreakdownRow[]> {
  const orgId = useActiveOrg().orgId;
  const { path, enabled, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.pageTrafficBreakdown(orgId, siteUuid ?? '', path, dimension, keyOpts),
    enabled: !!siteUuid && !!path && (enabled ?? true),
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelBreakdownRow[]>(
        `/v1/sites/${siteUuid}/pixel/page-breakdown${qs({
          path,
          dimension,
          days: keyOpts.days,
          limit: keyOpts.limit,
          sort: keyOpts.sort,
        })}`,
      ),
  });
}

export function usePagePixelSessions(
  siteUuid: string | null,
  opts: { path: string; days?: number; limit?: number; offset?: number },
): UseQueryResult<PixelPageSessionRow[]> {
  const orgId = useActiveOrg().orgId;
  const { path, ...keyOpts } = opts;
  return useQuery({
    queryKey: pixelKeys.pageSessions(orgId, siteUuid ?? '', path, keyOpts),
    enabled: !!siteUuid && !!path,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPageSessionRow[]>(
        `/v1/sites/${siteUuid}/pixel/page-sessions${qs({
          path,
          days: keyOpts.days,
          limit: keyOpts.limit,
          offset: keyOpts.offset,
        })}`,
      ),
  });
}

/** Ordered event stream for one session. `around` is the session's
 *  started_at from the sessions list — it bounds the raw scan to ±1 day
 *  (the events sorting key has no session_uuid), so it's required. */
export function usePixelSessionEvents(
  siteUuid: string | null,
  opts: { session: string; around: string | undefined },
): UseQueryResult<PixelSessionEventRow[]> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.sessionEvents(orgId, siteUuid ?? '', opts.session, {
      around: opts.around,
    }),
    enabled: !!siteUuid && !!opts.session && !!opts.around,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelSessionEventRow[]>(
        `/v1/sites/${siteUuid}/pixel/session-events${qs({
          session: opts.session,
          around: opts.around,
        })}`,
      ),
  });
}

export function useSiteTopEvents(
  siteUuid: string | null,
  opts: { days?: number; limit?: number; event_kind?: string } = {},
): UseQueryResult<PixelTopEventsRow[]> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.topEvents(orgId, siteUuid ?? '', opts),
    enabled: !!siteUuid,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelTopEventsRow[]>(`/v1/sites/${siteUuid}/pixel/top-events${qs(opts)}`),
  });
}

/**
 * Polling recipe for the pixel real-time surfaces (live).
 *
 * Two halves, applied to every poller so the behaviour is consistent:
 *   - `refetchIntervalInBackground: false` — the interval stops while
 *     the tab is hidden, so a backgrounded dashboard burns zero reads.
 *   - `refetchOnWindowFocus: true` — TanStack's focus manager fires on
 *     `visibilitychange`, so the moment the tab is foregrounded again a
 *     stale poller refetches immediately rather than waiting up to a
 *     full interval. (The app-wide default is `false` — dashboards
 *     aren't collaborative — so each poller opts in explicitly.)
 *
 * The previous `useSitePixelLive` did the focus-refresh by hand with a
 * `visibilitychange` listener + `invalidateQueries`; `refetchOnWindowFocus`
 * is the idiomatic equivalent (it respects `staleTime`, so it won't
 * hammer a surface that was just looked at) and drops the bespoke
 * effect.
 */
const POLLER_FOCUS_OPTS = {
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
} as const;

/** Live-visitors pill poll interval. The pill always lives in the
 * shell, so this is the dashboard's single biggest source of Tinybird
 * reads — one open tab at 5s burned ~720 reads/hr and tripped the
 * 1000/day workspace limit with a single operator. The underlying
 * window (active in the last 5 min) only moves on a minute scale, so
 * 30s is plenty live while cutting that load 6×. */
const LIVE_POLL_MS = 30_000;

// TODO(billing): re-enable the live-visitors poll once the Tinybird
// plan is paid. Flip this back to `true`.
//
// Turned OFF on 2026-05-30: the pill is always mounted in the shell, so
// it polled /pixel/live every 30s = ~2,880 Tinybird reads/day from a
// single open, focused tab — on its own ~3× the 1000/day workspace
// limit, with zero user activity. Gating `enabled` off stops the query
// entirely (no poll, no initial fetch); the badge then renders nothing
// (it's mounted with `hideWhenInactive`). The proper fix is to source
// the live count from an edge Durable Object instead of Tinybird — see
// the diagnosis in the PR description.
const LIVE_POLLING_ENABLED = false;

/**
 * Live-visitors pill — polls every {@link LIVE_POLL_MS} while the tab
 * is visible (see {@link POLLER_FOCUS_OPTS}): the interval halts when
 * backgrounded and a refetch fires on tab-return. `staleTime: 0` makes
 * that focus refetch unconditional (the count is always "stale enough"
 * to be worth refreshing the instant you look).
 *
 * Defaults to off via {@link LIVE_POLLING_ENABLED} — see that flag.
 *
 * `opts.enabled` overrides the flag for callers whose cost profile is
 * different from the always-mounted pill's. The flag stays off even on
 * the paid plan: the pill polls on *every* page for *every* open tab
 * regardless of whether anyone cares about the number, which is data
 * nobody asked for at ~2,880 reads/day/tab. A surface the user
 * deliberately navigated to in order to watch is a different bargain,
 * so `/traffic/live` opts in and caps its own session (see that route);
 * the pill then renders from that same cache entry for free.
 * Anything mounted globally must leave this alone.
 */
export function useSitePixelLive(
  siteUuid: string | null,
  opts: { enabled?: boolean; intervalMs?: number } = {},
): UseQueryResult<PixelLive> {
  const orgId = useActiveOrg().orgId;
  const intervalMs = opts.intervalMs ?? LIVE_POLL_MS;
  return useQuery({
    queryKey: pixelKeys.live(orgId, siteUuid ?? ''),
    enabled: (opts.enabled ?? LIVE_POLLING_ENABLED) && !!siteUuid,
    staleTime: 0,
    ...POLLER_FOCUS_OPTS,
    refetchInterval: () => {
      if (typeof document === 'undefined') return false;
      if (document.visibilityState !== 'visible') return false;
      return intervalMs;
    },
    queryFn: () => apiFetch<PixelLive>(`/v1/sites/${siteUuid}/pixel/live`),
  });
}

export function useSitePixelHealth(
  siteUuid: string | null,
  opts: { enabled?: boolean } = {},
): UseQueryResult<PixelHealthToday> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.health(orgId, siteUuid ?? ''),
    enabled: !!siteUuid && (opts.enabled ?? true),
    // No background poll. The old 30s interval was a steady Tinybird
    // drip: it matched the 30s `/health` edge-cache TTL, so each poll
    // tended to land as the entry expired → a live read every 30s per
    // open dashboard (the same failure mode that got `/live` disabled).
    // Health still refreshes on mount and when the user returns to the
    // tab (gated by the 30s staleTime), which is enough to flip the
    // install-state gate once the first pixel event lands.
    refetchOnWindowFocus: true,
    queryFn: () => apiFetch<PixelHealthToday>(`/v1/sites/${siteUuid}/pixel/health`),
  });
}

// ─── Page-scoped hooks ────────────────────────────────────────────────────

export function usePagePixelPerf(
  pageUuid: string | null,
  opts: CommonOpts = {},
): UseQueryResult<PixelPerfRow[]> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.pagePerf(orgId, pageUuid ?? '', opts),
    enabled: !!pageUuid,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPerfRow[]>(
        `/v1/pages/${pageUuid}/pixel/perf${qs({ days: opts.days, form_factors: opts.formFactors })}`,
      ),
  });
}

export function usePagePixelPerfTrend(
  pageUuid: string | null,
  opts: CommonOpts = {},
): UseQueryResult<PixelPerfTrendRow[]> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.pagePerfTrend(orgId, pageUuid ?? '', opts),
    enabled: !!pageUuid,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelPerfTrendRow[]>(
        `/v1/pages/${pageUuid}/pixel/perf-trend${qs({ days: opts.days, form_factors: opts.formFactors })}`,
      ),
  });
}

export function usePagePixelTopEvents(
  pageUuid: string | null,
  opts: { days?: number; limit?: number; event_kind?: string } = {},
): UseQueryResult<PixelTopEventsRow[]> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.pageTopEvents(orgId, pageUuid ?? '', opts),
    enabled: !!pageUuid,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelTopEventsRow[]>(`/v1/pages/${pageUuid}/pixel/top-events${qs(opts)}`),
  });
}

// ─── Bundle hook (the site-home cold-load fast path) ──────────────────────

interface BundleOpts {
  surfaces?: readonly string[];
  days?: number;
  formFactors?: string[];
}

export function useSitePixelBundle(
  siteUuid: string | null,
  opts: BundleOpts = {},
): UseQueryResult<PixelBundle> {
  const orgId = useActiveOrg().orgId;
  return useQuery({
    queryKey: pixelKeys.bundle(orgId, siteUuid ?? '', opts),
    enabled: !!siteUuid,
    placeholderData: keepPreviousData,
    queryFn: () =>
      apiFetch<PixelBundle>(
        `/v1/sites/${siteUuid}/pixel/bundle${qs({
          surfaces: opts.surfaces,
          days: opts.days,
          form_factors: opts.formFactors,
        })}`,
      ),
  });
}
